import {composeModAtlas} from '../../static/mod/composeModAtlas.js';

export const makeModCanvas = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

export async function decodeModImage(blob) {
    if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
    const url = URL.createObjectURL(blob);
    try {
        const image = new Image();
        await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; image.src = url; });
        return image;
    } finally { URL.revokeObjectURL(url); }
}

export class ModAtlasBuilder {
    constructor() { this.pending = new Map(); this.sequence = 0; this.failed = false; this.disposed = false; }

    async compose(payload) {
        if (this.disposed) throw new DOMException('Mod cancelled', 'AbortError');
        if (!this.failed && typeof Worker !== 'undefined' && typeof createImageBitmap === 'function') {
            let base;
            try {
                if (!this.worker) {
                    // Static worker modules are copied unchanged in both build modes.
                    this.worker = new Worker(new URL('mod/modAtlasWorker.js', document.baseURI), {type: 'module'});
                    this.worker.onmessage = ({data}) => {
                        const pending = this.pending.get(data.id);
                        this.pending.delete(data.id);
                        if (!pending) { data.result?.bitmap?.close(); return; }
                        data.error ? pending.reject(new Error(data.error)) : pending.resolve(data.result);
                    };
                    this.worker.onerror = event => { event.preventDefault(); this.fail(new Error(event.message || 'Atlas worker unavailable')); };
                    this.worker.onmessageerror = () => this.fail(new Error('Invalid atlas worker result'));
                }
                base = await createImageBitmap(payload.base);
                if (this.disposed) throw new DOMException('Mod cancelled', 'AbortError');
                const id = this.sequence++;
                const result = await new Promise((resolve, reject) => {
                    this.pending.set(id, {resolve, reject});
                    // Transfer the copy, keeping Pixi's original texture untouched.
                    this.worker.postMessage({id, payload: {...payload, base}}, [base]);
                });
                const canvas = makeModCanvas(payload.width, payload.height);
                canvas.getContext('2d').drawImage(result.bitmap, 0, 0);
                result.bitmap.close();
                return {...result, bitmap: undefined, canvas, worker: true};
            } catch (error) {
                this.fail(error);
                if (this.disposed) throw error;
            } finally { base?.close(); }
        }
        const result = await composeModAtlas(payload, {
            makeCanvas: makeModCanvas, decode: decodeModImage,
            encode: canvas => new Promise(resolve => canvas.toBlob(resolve, 'image/png')),
            cancelled: () => this.disposed,
        });
        return {...result, worker: false};
    }

    fail(error) {
        this.failed = true;
        this.worker?.terminate();
        this.worker = null;
        for (const pending of this.pending.values()) pending.reject(error);
        this.pending.clear();
    }

    dispose() {
        this.disposed = true;
        this.fail(new DOMException('Mod cancelled', 'AbortError'));
    }
}
